import { Autocomplete, InputAdornment, TextField } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { setFromValueParam } from "lib/setFromEvent"
import { navigate } from "lib/routes/navigate"
import { SCHEDULE } from "library/constants.mjs"
import { splitId } from "library/database/split-id.mjs"
import { useDebouncedValue } from "lib/@hooks/useDebouncedValue"
import { useRegimeSearch } from "routes/regime/plugins/regime-search/use-regime-search"
import { RegimeSearchResult } from "routes/regime/plugins/regime-search/regime-search-result"
import { RegimeSearchGroup } from "routes/regime/plugins/regime-search/regime-search-group"
import { RegimeSearchResultsPanel } from "routes/regime/plugins/regime-search/regime-search-results-panel"
import {
    ALL_SCOPE,
    isSelectableScope,
    scopeLabelFor,
    scopeOf,
    SEARCH_SCOPES,
} from "routes/regime/plugins/regime-search/lib/search-scopes.js"
import { cleanLabel } from "routes/regime/plugins/regime-search/lib/search-labels.mjs"
import {
    breadcrumbsFor,
    groupLabelFrom,
    REGIMES_GROUP,
    REGIMES_GROUP_BREADCRUMBS,
} from "routes/regime/plugins/regime-search/lib/search-breadcrumbs.js"

export function RegimeSearch({ regimes, sx }) {
    const [query, setQuery, debouncedQuery] = useDebouncedValue("", 300)
    const [open, setOpen] = useState(false)
    const [searchScope, setSearchScope] = useState(ALL_SCOPE)
    const { searchResults, loading } = useRegimeSearch(regimes, debouncedQuery)
    const { sorted, groupLabels, groupBreadcrumbs } = useMemo(deriveGroups, [searchResults])
    const scopeCounts = useMemo(countScopes, [searchResults])
    const scoped = useMemo(filterByScope, [sorted, searchScope])

    return (
        <Bound
            searchScope={searchScope}
            setSearchScope={setSearchScope}
            scopeCounts={scopeCounts}
            resultsLoading={loading}
        >
            <Autocomplete
                data-cy="regime-search"
                disableClearable
                fullWidth
                sx={sx}
                options={scoped}
                loading={loading}
                inputValue={query}
                onInputChange={setFromValueParam(setQuery)}
                onChange={onSelect}
                open={open && debouncedQuery.trim().length > 0}
                onOpen={openResults}
                onClose={closeResults}
                filterOptions={(options) => options}
                clearOnBlur={false}
                forcePopupIcon={false}
                noOptionsText={noMatchesMessage()}
                ListboxProps={{ sx: { pt: 1 } }}
                getOptionLabel={optionLabel}
                groupBy={groupFor}
                PaperComponent={RegimeSearchResultsPanel}
                renderGroup={renderSearchGroup}
                renderOption={renderSearchResult}
                renderInput={renderSearchInput}
            />
        </Bound>
    )

    function renderSearchResult(resultProps, searchResult) {
        const { key, ...rest } = resultProps
        return <RegimeSearchResult key={searchResult.id ?? key} props={rest} item={searchResult} />
    }

    function renderSearchGroup(params) {
        return (
            <RegimeSearchGroup
                key={params.key}
                breadcrumbs={groupBreadcrumbsFor(params.group)}
                onNavigate={navigateToNode}
            >
                {params.children}
            </RegimeSearchGroup>
        )
    }

    function groupBreadcrumbsFor(group) {
        if (searchScope === "regime") return []
        return groupBreadcrumbs.get(group) ?? []
    }

    function countScopes() {
        const counts = { [ALL_SCOPE]: searchResults.length }
        for (const searchResult of searchResults) {
            const scope = scopeOf(searchResult)
            counts[scope] = (counts[scope] ?? 0) + 1
        }
        return counts
    }

    function filterByScope() {
        if (searchScope === ALL_SCOPE) return sorted
        return sorted.filter((searchResult) => scopeOf(searchResult) === searchScope)
    }

    function noMatchesMessage() {
        const term = debouncedQuery.trim()
        if (searchScope === ALL_SCOPE) return `No results for "${term}"`
        return `No ${scopeLabelFor(searchScope).toLowerCase()} match "${term}"`
    }

    function deriveGroups() {
        const groupLabels = new Map()
        const groupBreadcrumbs = new Map([[REGIMES_GROUP, REGIMES_GROUP_BREADCRUMBS]])
        for (const searchResult of searchResults) {
            const breadcrumbs = breadcrumbsFor(searchResult)
            const label = groupLabelFrom(breadcrumbs)
            groupLabels.set(searchResult, label)
            if (!groupBreadcrumbs.has(label)) groupBreadcrumbs.set(label, breadcrumbs)
        }
        return { sorted: [...searchResults].sort(byGroup), groupLabels, groupBreadcrumbs }

        function byGroup(a, b) {
            const labelA = groupLabels.get(a)
            const labelB = groupLabels.get(b)
            if (labelA === labelB) return 0
            if (labelA === REGIMES_GROUP) return -1
            if (labelB === REGIMES_GROUP) return 1
            return labelA.localeCompare(labelB)
        }
    }

    function groupFor(searchResult) {
        return groupLabels.get(searchResult) ?? REGIMES_GROUP
    }

    function renderSearchInput(params) {
        return (
            <TextField
                {...params}
                data-cy="regime-search-input"
                variant="outlined"
                size="small"
                onKeyDown={onSearchKeyDown}
                placeholder="Search regimes, facilities, schedules and assets"
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify icon="eva:search-fill" sx={{ color: "text.disabled", width: 20, height: 20 }} />
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    function onSelect(event, value) {
        if (!value) return
        openResult(value)
    }

    function openResult(searchResult) {
        if (searchResult.isAsset) {
            const [group, id] = searchResult.scheduleId.split("!")
            navigate(`/app/schedules/${group}~${searchResult.regimeId}!${id}`)
            return
        }
        if (searchResult.type === SCHEDULE) {
            navigate(`/app/schedules/${splitId(searchResult.id).id}`)
            return
        }
        navigateToNode(searchResult)
    }

    function navigateToNode(item) {
        navigate(`/app/schedules?id=${encodeURIComponent(item.id)}`)
        closeResults()
    }

    function onSearchKeyDown(event) {
        if (event.key !== "Tab") return
        if (!open || !debouncedQuery.trim().length) return
        event.preventDefault()
        cycleScope(event.shiftKey ? -1 : 1)
    }

    function cycleScope(direction) {
        const ids = SEARCH_SCOPES.map((scope) => scope.id)
        let index = ids.indexOf(searchScope)
        for (let step = 0; step < ids.length; step++) {
            index = (index + direction + ids.length) % ids.length
            if (isSelectableScope(ids[index], scopeCounts, loading)) {
                setSearchScope(ids[index])
                return
            }
        }
    }

    function openResults() {
        setOpen(true)
    }

    function closeResults() {
        setOpen(false)
        setSearchScope(ALL_SCOPE)
    }
}

function optionLabel(searchResult) {
    return cleanLabel(searchResult.label ?? searchResult.data?.name ?? "")
}
